import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { Workspace } from '@/types/member';
import type { Segment } from '@/types/segment';
import { hasSupabase, getSupabase } from '@/services/supabaseService';
import { groupWorkspace } from '@/boot/posthog';

const LS_KEY = 'quorum.activeWorkspaceId';

export const useWorkspaceStore = defineStore('workspace', () => {
  const current = ref<Workspace | null>(null);
  const segments = ref<Segment[]>([]);
  const workspaceId = ref<string | null>(
    // Hydrate from localStorage for instant restore before Supabase round-trip (H2)
    (typeof localStorage !== 'undefined' && localStorage.getItem(LS_KEY)) || null
  );
  const loading = ref(false);

  // M2 — workspace list state
  const list = ref<Workspace[]>([]);
  const loadingList = ref(false);
  const mutating = ref(false);

  // M1 — Keep current in sync with list when workspaceId or list changes.
  // Guard against id mismatch to avoid infinite re-assignment loops.
  watch(
    [list, workspaceId],
    ([newList, newId]) => {
      if (!newId || newList.length === 0) return;
      if (current.value?.id === newId) return;
      const found = newList.find((w) => w.id === newId);
      if (found) current.value = found;
    },
    { immediate: true, flush: 'sync' }
  );

  // DIAG TEMP — remove after M0 (fix-project-detail-recursive)
  // Logs every mutation of workspaceId (including same-value reassignments via flush: 'sync').
  // Used to confirm H1 (cycle via :key + workspaceId reassignment in ensureWorkspace).
  watch(
    workspaceId,
    (v, prev) => {
      // eslint-disable-next-line no-console
      console.log('[fix-recursive][workspaceStore] workspaceId mutation', prev, '->', v, '(same?', prev === v, ')');
      // eslint-disable-next-line no-console
      console.trace('[fix-recursive][workspaceStore] mutation trace');
    },
    { flush: 'sync' }
  );
  // END DIAG TEMP

  const setWorkspace = (ws: Workspace | null) => {
    current.value = ws;
    workspaceId.value = ws?.id ?? null;
    // Persist active workspace id for fast hydration on next load (H2)
    if (typeof localStorage !== 'undefined') {
      if (ws?.id) {
        localStorage.setItem(LS_KEY, ws.id);
      } else {
        localStorage.removeItem(LS_KEY);
      }
    }
  };

  const setSegments = (next: Segment[]) => {
    segments.value = next;
  };

  /**
   * D5 — Resolve the current member's workspace from Supabase. Required before
   * any page-level fetch that uses workspace_id as a filter.
   */
  const ensureWorkspace = async (userId: string): Promise<string | null> => {
    // DIAG TEMP — remove after M0 (fix-project-detail-recursive)
    // eslint-disable-next-line no-console
    console.log('[fix-recursive][workspaceStore] ensureWorkspace ENTER, current workspaceId =', workspaceId.value);
    // END DIAG TEMP
    if (workspaceId.value) {
      // DIAG TEMP — remove after M0
      // eslint-disable-next-line no-console
      console.log('[fix-recursive][workspaceStore] ensureWorkspace EARLY RETURN (already set) ->', workspaceId.value);
      // END DIAG TEMP
      return workspaceId.value;
    }
    if (!hasSupabase()) return null;
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from('members')
      .select('workspace_id, workspaces:workspace_id(id, name, owner_member_id, created_at, updated_at)')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return null;
    // DIAG TEMP — remove after M0
    // eslint-disable-next-line no-console
    console.log('[fix-recursive][workspaceStore] ensureWorkspace ASSIGN workspaceId.value =', data.workspace_id, '(was', workspaceId.value, ')');
    // END DIAG TEMP
    workspaceId.value = data.workspace_id as string;
    const ws = (data as unknown as { workspaces: Workspace | null }).workspaces;
    if (ws) {
      current.value = {
        id: ws.id,
        name: ws.name,
        ownerMemberId: (ws as unknown as { owner_member_id: string }).owner_member_id ?? '',
        createdAt: (ws as unknown as { created_at: string }).created_at ?? '',
        updatedAt: (ws as unknown as { updated_at: string }).updated_at ?? ''
      };
    }
    if (typeof localStorage !== 'undefined' && workspaceId.value) {
      localStorage.setItem(LS_KEY, workspaceId.value);
    }
    // CA-7: associate the session with this workspace group in PostHog.
    if (workspaceId.value) {
      groupWorkspace(workspaceId.value, { plan: 'unknown' });
    }
    return workspaceId.value;
  };

  /**
   * M2 — Load all workspaces accessible to the current member via member_workspaces.
   * Re-throws on error so the caller (AppLayout / ManageWorkspacesPage) can handle it.
   */
  const loadList = async (): Promise<void> => {
    if (!hasSupabase()) return;
    loadingList.value = true;
    try {
      const { listMemberWorkspacesWithStats } = await import('@/services/workspacesService');
      list.value = await listMemberWorkspacesWithStats();
    } finally {
      loadingList.value = false;
    }
  };

  /**
   * M2 — Create a new workspace and refresh the list.
   */
  const createWorkspace = async (name: string): Promise<Workspace> => {
    mutating.value = true;
    try {
      const { createWorkspace: svcCreate } = await import('@/services/workspacesService');
      const ws = await svcCreate(name);
      list.value = [...list.value, ws];
      return ws;
    } finally {
      mutating.value = false;
    }
  };

  /**
   * M2 — Rename a workspace by id.
   */
  const renameWorkspace = async (id: string, name: string): Promise<Workspace> => {
    mutating.value = true;
    try {
      const { renameWorkspace: svcRename } = await import('@/services/workspacesService');
      const ws = await svcRename(id, name);
      const idx = list.value.findIndex((w) => w.id === id);
      if (idx !== -1) list.value[idx] = ws;
      if (current.value?.id === id) current.value = ws;
      return ws;
    } finally {
      mutating.value = false;
    }
  };

  /**
   * M2 — Delete a workspace by id.
   */
  const deleteWorkspace = async (id: string): Promise<void> => {
    mutating.value = true;
    try {
      const { deleteWorkspace: svcDelete } = await import('@/services/workspacesService');
      await svcDelete(id);
      list.value = list.value.filter((w) => w.id !== id);
    } finally {
      mutating.value = false;
    }
  };

  /**
   * M2 — Switch the active workspace. Persists to localStorage and Supabase.
   * Router navigation is handled by the caller (useWorkspaceSwitch composable).
   */
  const switchWorkspace = async (id: string): Promise<void> => {
    const ws = list.value.find((w) => w.id === id) ?? null;
    setWorkspace(ws);
    // CA-7: re-group on workspace switch so subsequent events carry the new workspace context.
    groupWorkspace(id, { plan: 'unknown' });
    if (!hasSupabase()) return;
    try {
      const { setActiveWorkspace } = await import('@/services/workspacesService');
      await setActiveWorkspace(id);
    } catch {
      // Best-effort: local state already updated
    }
  };

  const segmentByShortId = computed(() => {
    const map: Record<string, Segment> = {};
    for (const s of segments.value) map[s.shortId] = s;
    return map;
  });

  /**
   * Appends a new segment to the store without deduplication.
   *
   * Reserved for future real-time / collaborative editing features where
   * the server pushes a new segment event (e.g. Supabase Realtime INSERT
   * broadcast) and the client needs to reflect it immediately without
   * re-fetching the full list. Use `upsertSegment` for standard post-creation
   * flows where idempotency is required.
   */
  const addSegment = (seg: Segment): void => {
    segments.value.push(seg);
  };

  /** Inserts a segment if new (by id), or replaces the existing entry. */
  const upsertSegment = (seg: Segment): void => {
    const idx = segments.value.findIndex((s) => s.id === seg.id);
    if (idx === -1) {
      segments.value.push(seg);
    } else {
      segments.value[idx] = seg;
    }
  };

  /** Removes a segment by id. Symmetric with personasStore.removeItem. */
  const removeSegment = (id: string): void => {
    segments.value = segments.value.filter((s) => s.id !== id);
  };

  return {
    current,
    segments,
    workspaceId,
    loading,
    list,
    loadingList,
    mutating,
    setWorkspace,
    setSegments,
    ensureWorkspace,
    loadList,
    createWorkspace,
    renameWorkspace,
    deleteWorkspace,
    switchWorkspace,
    segmentByShortId,
    addSegment,
    upsertSegment,
    removeSegment
  };
});
